import { useCallback, useState, useRef, useEffect } from "react";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import InstagramPostEmbed from "./InstagramPostEmbed";
import type { InstagramPost } from "@/data/siteContent";

interface InstagramFeedSectionProps {
  dataKey?: string;
}

function extractShortcode(url: string): string | null {
  const match = url.match(/instagram\.com\/(?:[^/]+\/)?(?:p|reel|tv)\/([^/?#]+)/);
  return match?.[1] ?? null;
}

export default function InstagramFeedSection({ dataKey = "ma_instagram_posts" }: InstagramFeedSectionProps) {
  const { isAdmin, isEditing } = useAdmin();
  const { isDark } = useDarkMode();
  const [posts, setPosts] = useAdminStorage<InstagramPost[]>(dataKey, []);
  const [currentPage, setCurrentPage] = useState(0);
  const [newUrl, setNewUrl] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const addInputRef = useRef<HTMLInputElement>(null);

  const safePosts = Array.isArray(posts) ? posts : [];
  const postsPerPage = typeof window !== "undefined" && window.innerWidth < 640 ? 1 : 3;
  const totalPages = Math.max(1, Math.ceil(safePosts.length / postsPerPage));
  const visiblePosts = safePosts.slice(
    currentPage * postsPerPage,
    (currentPage + 1) * postsPerPage
  );

  const goPrev = () => setCurrentPage((p) => Math.max(0, p - 1));
  const goNext = () => setCurrentPage((p) => Math.min(totalPages - 1, p + 1));

  const addPost = useCallback(() => {
    const trimmed = newUrl.trim();
    if (!trimmed) return;
    const shortcode = extractShortcode(trimmed);
    if (!shortcode) {
      setUrlError("Invalid Instagram URL. Must be like https://www.instagram.com/p/ABC123/ or https://www.instagram.com/username/p/ABC123/");
      return;
    }
    const next: InstagramPost = {
      id: `ig-${shortcode}-${Date.now()}`,
      url: trimmed,
      caption: "",
    };
    setPosts([...safePosts, next]);
    setNewUrl("");
    setUrlError(null);
    const newTotal = Math.ceil((safePosts.length + 1) / postsPerPage);
    setCurrentPage(newTotal - 1);
  }, [newUrl, safePosts, setPosts, postsPerPage]);

  const removePost = useCallback((id: string) => {
    const next = safePosts.filter((p) => p.id !== id);
    setPosts(next);
    if (currentPage >= Math.ceil(next.length / postsPerPage)) {
      setCurrentPage(Math.max(0, Math.ceil(next.length / postsPerPage) - 1));
    }
  }, [safePosts, setPosts, currentPage, postsPerPage]);

  const movePost = useCallback((idx: number, dir: -1 | 1) => {
    const next = [...safePosts];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setPosts(next);
  }, [safePosts, setPosts]);

  const isAdminEditing = isAdmin && isEditing;

  const sectionBg = isDark ? "bg-gray-950" : "bg-gray-50";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const adminCardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const adminInputBg = isDark
    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500"
    : "bg-white border-gray-200 text-gray-800";

  if (safePosts.length === 0 && !isAdminEditing) {
    return (
      <section className={`py-16 px-4 md:px-6 ${sectionBg}`}>
        <div className="max-w-6xl mx-auto text-center">
          <p className={`text-sm ${subColor}`}>No Instagram posts configured yet.</p>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-16 px-4 md:px-6 ${sectionBg} relative`}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h2 className={`text-2xl md:text-3xl font-bold ${headingColor} mb-2`}>
            Follow Us on Instagram
          </h2>
          <p className={`text-sm ${subColor}`}>@makersalleyco</p>
        </div>

        {/* Admin inline grid — shows ALL posts with reorder controls */}
        {isAdminEditing && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-instagram-line text-pink-500"></i>
              <span className={`text-xs font-bold ${isDark ? "text-gray-300" : "text-gray-700"}`}>Manage Instagram Posts</span>
              <span className={`text-[10px] ${subColor} ml-auto`}>
                {safePosts.length} post{safePosts.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {safePosts.map((post, idx) => {
                const sc = extractShortcode(post.url);
                return (
                  <div key={post.id} className={`relative group/post rounded-xl border overflow-hidden ${adminCardBg}`}>
                    <div className="absolute top-2 right-2 z-10 flex items-center gap-1 opacity-0 group-hover/post:opacity-100 transition-opacity">
                      <button
                        onClick={() => movePost(idx, -1)}
                        disabled={idx === 0}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-white/90 border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 cursor-pointer shadow-sm"
                        title="Move left"
                      >
                        <i className="ri-arrow-left-line text-xs"></i>
                      </button>
                      <button
                        onClick={() => movePost(idx, 1)}
                        disabled={idx === safePosts.length - 1}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-white/90 border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 cursor-pointer shadow-sm"
                        title="Move right"
                      >
                        <i className="ri-arrow-right-line text-xs"></i>
                      </button>
                      <button
                        onClick={() => removePost(post.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-white/90 border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-600 cursor-pointer shadow-sm"
                        title="Remove post"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>

                    <div className="h-48 bg-gray-100 flex items-center justify-center">
                      <InstagramPostEmbed
                        postUrl={post.url}
                        caption={post.caption}
                        maxWidth={400}
                        className="w-full h-full"
                      />
                    </div>

                    <div className={`px-3 py-2 border-t flex items-center justify-between ${isDark ? "border-gray-700 bg-gray-800" : "border-gray-100 bg-white"}`}>
                      <span className={`text-[10px] font-semibold uppercase tracking-wide truncate max-w-[70%] ${subColor}`}>
                        {sc || "Post"}
                      </span>
                      <span className={`text-[10px] ${subColor}`}>#{idx + 1}</span>
                    </div>
                  </div>
                );
              })}

              {/* Add-new-post blank slot */}
              <div className={`rounded-xl border-2 border-dashed p-4 flex flex-col items-center justify-center gap-2 min-h-[200px] ${isDark ? "border-gray-600 bg-gray-800/50" : "border-gray-200 bg-gray-50/50"}`}>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-pink-100 text-pink-500">
                  <i className="ri-add-line text-lg"></i>
                </div>
                <p className={`text-xs font-semibold ${subColor}`}>Add Instagram Post</p>
                <div className="w-full flex gap-2 mt-1">
                  <input
                    ref={addInputRef}
                    type="text"
                    value={newUrl}
                    onChange={(e) => { setNewUrl(e.target.value); setUrlError(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter") addPost(); }}
                    placeholder="Paste URL and press Enter..."
                    className={`flex-1 border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-pink-200 ${adminInputBg}`}
                  />
                  <button
                    onClick={addPost}
                    className="bg-pink-500 hover:bg-pink-600 text-white font-bold text-xs px-3 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors flex-shrink-0"
                  >
                    Add
                  </button>
                </div>
                {urlError && <p className="text-red-500 text-[10px]">{urlError}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Visitor carousel (only when NOT in admin editing mode) */}
        {!isAdminEditing && (
          <>
            <div className="relative">
              {safePosts.length > postsPerPage && (
                <button
                  onClick={goPrev}
                  disabled={currentPage === 0}
                  className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full border shadow-sm hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all -ml-3 md:-ml-5 ${isDark ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-200 text-gray-600"}`}
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                </button>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center px-2">
                {visiblePosts.map((post) => (
                  <InstagramPostEmbed
                    key={post.id}
                    postUrl={post.url}
                    caption={post.caption}
                    maxWidth={400}
                    className="w-full"
                  />
                ))}
              </div>

              {safePosts.length > postsPerPage && (
                <button
                  onClick={goNext}
                  disabled={currentPage >= totalPages - 1}
                  className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full border shadow-sm hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all -mr-3 md:-mr-5 ${isDark ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-200 text-gray-600"}`}
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                      i === currentPage ? "bg-pink-500 w-4" : isDark ? "bg-gray-600 hover:bg-gray-500" : "bg-gray-300 hover:bg-gray-400"
                    }`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}